import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Sparkles, Link as LinkIcon, Target, TrendingUp, Clock,
  DollarSign, Layers, Brain, Users, Zap, Shield, CheckCircle,
  ArrowRight, BarChart3, Megaphone, Heart, Briefcase, Car, Truck, ShoppingBag
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const AIConsulting = () => {
  const [countersStarted, setCountersStarted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
            entry.target.classList.remove('opacity-0');
            if (entry.target.id === 'stats-section') {
              setCountersStarted(true);
            }
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.scroll-reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const consultingServices = [
    {
      icon: Sparkles,
      title: 'Generative AI Solution Development',
      description: 'From model training to UI/UX design and deployment, we provide end-to-end GenAI solution development — blending creativity with cutting-edge automation.'
    },
    {
      icon: LinkIcon,
      title: 'AI Integration',
      description: 'Seamlessly embed AI systems into your existing business infrastructure. Our experts ensure operational continuity and maximize efficiency during transformation.'
    },
    {
      icon: Target,
      title: 'AI Strategy Development',
      description: 'We engage with leadership teams to craft AI roadmaps, prioritize initiatives, and outline clear implementation timelines aligned with organizational goals.'
    }
  ];

  const industries = [
    { icon: Megaphone, name: 'Marketing & Advertising' },
    { icon: Heart, name: 'Healthcare & Wellness' },
    { icon: Briefcase, name: 'Fintech' },
    { icon: Car, name: 'Automotive' },
    { icon: Truck, name: 'Logistics & Supply Chain' },
    { icon: ShoppingBag, name: 'E-commerce & Retail' }
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Higher Revenue',
      description: 'Leverage AI to uncover new revenue streams, personalize offerings, and enhance customer experiences.'
    },
    {
      icon: Clock,
      title: 'Speedy Time to Value',
      description: 'Accelerate AI implementation with a clear roadmap that reduces time-to-market and maximizes ROI.'
    },
    {
      icon: DollarSign,
      title: 'Long-Term Cost Efficiency',
      description: 'Automate operations, streamline workflows, and reduce overhead while improving accuracy.'
    },
    {
      icon: Layers,
      title: 'Future-Ready Systems',
      description: 'Adopt scalable, adaptive AI systems that evolve with technology and market demands.'
    },
    {
      icon: BarChart3,
      title: 'Greater ROI',
      description: 'Every project is guided by clear business KPIs, ensuring value-driven results beyond experimentation.'
    },
    {
      icon: Brain,
      title: 'Smarter Decision Making',
      description: 'Empower teams with predictive insights and real-time intelligence to make faster, data-backed decisions.'
    }
  ];

  const whyHireExperts = [
    'We assess and identify AI opportunities that deliver maximum business impact.',
    'We recommend scalable, industry-aligned AI systems based on your goals.',
    'We create implementation plans that seamlessly integrate with your existing workflows.',
    'We deliver ethical, efficient, and measurable AI solutions that align with your KPIs.'
  ];

  const consultingProcess = [
    { title: 'Initial Discovery & Needs Assessment', description: 'Identify challenges, opportunities, and goals through consultation.' },
    { title: 'Strategic AI Planning', description: 'Develop an AI roadmap aligned with your goals, budget, and risk factors.' },
    { title: 'Data Audit & Preparation', description: 'Evaluate and organize data for readiness and integrity.' },
    { title: 'Industry-Specific Model Design', description: 'Create and fine-tune AI models tailored to your business domain.' },
    { title: 'AI Model Deployment & Alignment', description: 'Integrate models into your ecosystem for operational excellence.' },
    { title: 'AI-Driven Solution Implementation', description: 'Convert prototypes into live, functional AI systems.' },
    { title: 'Infrastructure Integration', description: 'Seamlessly integrate new technologies with existing IT systems.' },
    { title: 'Continuous Monitoring & Support', description: 'Provide post-deployment optimization and improvement.' }
  ];

  const stats = [
    { value: 59, suffix: '%', label: 'Organizations report revenue growth due to AI adoption' },
    { value: 67.2, suffix: 'B', label: 'Invested in AI in the US alone' },
    { value: 55, suffix: '%', label: 'Enterprises use AI in at least one business unit' }
  ];

  const whyChoose = [
    {
      icon: Target,
      title: 'Business-First Approach',
      description: 'We begin with your goals, not the tech. Our strategies solve real problems, enhance operations, and deliver measurable impact.'
    },
    {
      icon: Brain,
      title: 'Deep Technical Expertise',
      description: 'Our specialists in Generative AI, ML, and NLP bridge the gap between innovation and implementation.'
    },
    {
      icon: Zap,
      title: 'Fast, Scalable Execution',
      description: 'We execute with agility, ensuring faster rollouts without compromising quality. From pilot to enterprise deployment, we scale as you grow.'
    }
  ];

  return (
    <div className="bg-gray-50 text-gray-900 overflow-hidden">
      <Navbar />

      {/* Hero Section */}
      <section className="min-h-[50vh] flex items-center justify-center relative px-6 lg:px-12 pt-24 pb-12">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-100 to-white">
          <div className="absolute inset-0 opacity-30">
            <div
              className="w-full h-full"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233B82F6' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                backgroundSize: '60px 60px'
              }}
            ></div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/50 to-white"></div>

          {/* Floating particles */}
          <div className="absolute top-20 left-10 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl animate-float-delayed"></div>
        </div>

        <div className="max-w-5xl mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-light tracking-tight mb-8 leading-[1.1] text-gray-900 animate-fade-in" style={{ fontWeight: 300 }}>
            AI Consulting
            <br />
            <span className="text-blue-600">Services</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-12 leading-relaxed font-light animate-fade-in" style={{ animationDelay: '200ms' }}>
            Transform ideas into intelligent, scalable, and profitable ventures powered by AI.
            Our consulting experts help you identify impactful use cases, build roadmaps, and integrate AI systems that deliver measurable outcomes.
          </p>
          <div className="relative inline-block group">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-sky-500 rounded-xl blur opacity-30 group-hover:opacity-60 transition-opacity duration-500"></div>
            <Button
              size="lg"
              className="relative bg-gradient-to-r from-blue-500 to-sky-500 hover:from-sky-500 hover:to-blue-500 text-white px-8 py-6 shadow-lg hover:shadow-2xl transition-all duration-300"
            >
              <span className="relative z-10">Consult Our AI Expert</span>
              <div className="absolute inset-0 rounded-xl overflow-hidden">
                <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
              </div>
            </Button>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-32 px-6 lg:px-12 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            {/* Left Content */}
            <div className="scroll-reveal opacity-0">
              <p className="text-blue-500 text-sm uppercase tracking-wider mb-4">Overview</p>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 leading-tight">
                What is AI Consulting?
              </h2>
              <div className="space-y-6 text-gray-600 text-lg leading-relaxed">
                <p>
                  AI Consulting is the practice of helping organizations adopt, deploy, and optimize artificial intelligence strategically.
                  It involves evaluating readiness, identifying opportunities, selecting the right tools, and aligning AI initiatives with core business goals.
                </p>
                <p>
                  At ThinkVerse.AI, we combine intelligent automation, analytics, and machine learning to help companies scale operations, boost productivity, and gain competitive advantage.
                </p>
              </div>
            </div>

            {/* Right Visual */}
            <div className="scroll-reveal opacity-0 flex items-center justify-center">
              <div className="relative w-80 h-80">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute inset-10 border-4 border-blue-500/30 rounded-full animate-spin-slow"></div>
                <div className="absolute inset-20 border-4 border-blue-500/40 rounded-full animate-spin-reverse"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Users size={80} className="text-blue-500" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Consulting Services Section */}
      <section className="py-32 px-6 lg:px-12 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20 scroll-reveal opacity-0">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Achieve Your Business Goals with
              <br />
              <span className="text-blue-500">AI Consulting</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              We deliver scalable AI solutions and digital strategies that transform ideas into intelligent systems with measurable value.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {consultingServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card
                  key={index}
                  className="bg-white border-gray-30000 hover:border-blue-500/50 backdrop-blur-sm transition-all duration-300 hover:scale-105 scroll-reveal opacity-0 group"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <CardContent className="p-8">
                    <div className="mb-6 text-blue-500 group-hover:scale-110 transition-transform duration-300">
                      <Icon size={48} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 text-gray-900">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center scroll-reveal opacity-0">
            <Button
              size="lg"
              className="bg-gradient-to-r from-blue-500 to-blue-500 hover:from-blue-500 hover:to-blue-500 text-gray-900 px-8 py-6 shadow-[0_0_30px_rgba(74,144,226,0.4)] hover:shadow-[0_0_40px_rgba(74,144,226,0.6)] transition-all duration-300"
            >
              Book a Strategy Session <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* Industry Applications Section */}
      <section className="py-32 px-6 lg:px-12 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 scroll-reveal opacity-0">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Implementing AI Consulting
              <br />
              <span className="text-blue-500">Across Industries</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Our experienced consultants have successfully implemented AI strategies across diverse industries — driving innovation, automation, and measurable growth.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <div
                  key={index}
                  className="flex flex-col items-center justify-center p-8 bg-white rounded-lg border border-gray-30000 hover:border-blue-500/50 transition-all duration-300 hover:scale-105 scroll-reveal opacity-0"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <Icon size={40} className="text-blue-500 mb-4" />
                  <span className="text-gray-700 text-center font-medium">{industry.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-32 px-6 lg:px-12 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 scroll-reveal opacity-0">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Benefits of
              <br />
              <span className="text-blue-500">AI Consulting</span>
            </h2>
            <p className="text-gray-600 text-lg">
              Turn AI Strategy into Real, Measurable Business Results.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card
                  key={index}
                  className="bg-white border-gray-30000 hover:border-blue-500/50 backdrop-blur-sm transition-all duration-300 hover:scale-105 scroll-reveal opacity-0"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <CardContent className="p-8">
                    <div className="mb-6 text-blue-500">
                      <Icon size={40} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4 text-gray-900">{benefit.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Block Section */}
      <section className="py-32 px-6 lg:px-12 bg-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-blue-500"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10 scroll-reveal opacity-0">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Want to Work
            <br />
            <span className="text-blue-500">With Us?</span>
          </h2>
          <p className="text-gray-600 text-lg mb-8 leading-relaxed">
            Ready to explore the possibilities of AI for your business? Let's collaborate to design intelligent, future-ready systems.
          </p>
          <Button
            size="lg"
            className="bg-blue-500 hover:bg-blue-600 text-white px-10 py-6 shadow-[0_0_30px_rgba(74,144,226,0.4)] hover:shadow-[0_0_40px_rgba(74,144,226,0.6)] transition-all duration-300"
          >
            Consult Our AI Expert
          </Button>
        </div>
      </section>

      {/* Why Hire Experts Section */}
      <section className="py-32 px-6 lg:px-12 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            {/* Left Content */}
            <div className="scroll-reveal opacity-0">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 leading-tight">
                Why Hire
                <br />
                <span className="text-blue-500">AI Consulting Experts?</span>
              </h2>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                With ThinkVerse.AI, align your business vision with intelligent, impactful AI solutions designed for growth.
              </p>
              
              <div className="space-y-6">
                {whyHireExperts.map((point, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-500 font-bold">
                      {index + 1}
                    </div>
                    <p className="text-gray-700 leading-relaxed pt-1">{point}</p>
                  </div>
                ))}
              </div>

              <Button
                size="lg"
                className="mt-8 bg-blue-500 hover:bg-blue-600 text-white px-8 py-6"
              >
                Get in Touch Today
              </Button>
            </div>

            {/* Right Visual */}
            <div className="scroll-reveal opacity-0">
              <div className="relative w-full h-96 bg-gradient-to-br from-blue-500/10 to-blue-500/10 rounded-lg overflow-hidden border border-gray-30000">
                <div className="absolute inset-0 flex items-center justify-center">
                  <Shield size={120} className="text-blue-500 opacity-30" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Consulting Process Section */}
      <section className="py-32 px-6 lg:px-12 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 scroll-reveal opacity-0">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Our AI Consulting
              <br />
              <span className="text-blue-500">Workflow</span>
            </h2>
            <p className="text-gray-600 text-lg">
              A strategic approach from discovery to deployment.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {consultingProcess.map((step, index) => (
              <div
                key={index}
                className="relative p-6 bg-gradient-to-br from-white/5 to-white/[0.02] border border-gray-30000 rounded-lg hover:border-blue-500/50 transition-all duration-300 scroll-reveal opacity-0"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="absolute top-4 right-4 text-blue-500/30 text-2xl font-bold">
                  {String(index + 1).padStart(2, '0')}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3 pr-12">{step.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Statistics Section */}
      <section id="stats-section" className="py-32 px-6 lg:px-12 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 scroll-reveal opacity-0">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              AI in 2025 —
              <br />
              <span className="text-blue-500">The Future is Now</span>
            </h2>
            <p className="text-gray-600 text-lg">
              Adoption and Impact Metrics (Source: Stanford HAI Annual Report)
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <Card
                key={index}
                className="bg-gradient-to-br from-blue-500/10 to-blue-500/10 border-blue-500/30 backdrop-blur-sm transition-all duration-300 hover:scale-105 scroll-reveal opacity-0"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardContent className="p-8 text-center">
                  <div className="text-5xl md:text-6xl font-bold text-blue-500 mb-4">
                    {countersStarted ? `${stat.value}${stat.suffix}` : '0'}
                  </div>
                  <p className="text-gray-700 leading-relaxed">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose ThinkVerse.AI Section */}
      <section className="py-32 px-6 lg:px-12 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 scroll-reveal opacity-0">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Why Choose ThinkVerse.AI for
              <br />
              <span className="text-blue-500">AI Consulting?</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Our consulting process unites technical depth, ethical frameworks, and agile execution for intelligent transformation.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {whyChoose.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="scroll-reveal opacity-0 p-8 bg-white border border-gray-30000 rounded-lg hover:border-blue-500/50 transition-all duration-300"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="mb-6">
                    <Icon size={40} className="text-blue-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 bg-gradient-to-r from-blue-500 to-blue-500 bg-clip-text text-transparent">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-32 px-6 lg:px-12 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 border-2 border-blue-500 rounded-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10 scroll-reveal opacity-0">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Looking for a Trusted
            <br />
            <span className="text-blue-500">AI Consulting Partner?</span>
          </h2>
          <p className="text-gray-600 text-lg mb-8 leading-relaxed">
            Let's create intelligent strategies that transform your vision into scalable AI-powered reality.
          </p>
          <Button
            size="lg"
            className="bg-gradient-to-r from-blue-500 to-blue-500 hover:from-blue-500 hover:to-blue-500 text-gray-900 px-10 py-6 shadow-[0_0_30px_rgba(74,144,226,0.4)] hover:shadow-[0_0_40px_rgba(74,144,226,0.6)] transition-all duration-300"
          >
            Start Your AI Journey <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AIConsulting;
